import OrganisationLayout from "../../components/organisation/layout/OrganisationLayout";
import { Spinner } from "../../components/base";
import { useState, useEffect } from "react";
import { BiSearchAlt } from "react-icons/bi";
import { findFilteredOrganisationProducts } from "../../store/slices/organisation/action";
import GreenDotIcon from "../../assets/img/GreenDotIcon.svg";
import { CurrencySign, formatAmount } from "../../utils/helper";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { useAppDispatch, useAppSelector } from "../../store";
import BaseToggleButton from "../../components/base/BaseToggleButton";
import { ProductType } from "../../enums/product-type.enum";

function PriceList() {
  const { priceListData } = useAppSelector(
    (state) => state.organisationReducer
  );
  const productTypes = [
    { id: "Package", name: "Packages" },
    { id: "Panel", name: "Individual Lab Test" },
  ];

  const headerStyle = {
    color: "#0C4654",
    fontSize: "0.875rem",
    fontWeight: "500",
    backgroundColor: "transparent",
  };
  const bodyStyle = {
    color: "#0C4654",
    fontSize: "1rem",
    fontWeight: "500",
    backgroundColor: "transparent",
  };

  const dispatch = useAppDispatch();
  const [productType, setProductType] = useState<{
    id: ProductType | string;
    name: string;
  }>({
    id: ProductType.Package,
    name: "Packages",
  });
  const [isLoading, setIsLoading] = useState(false);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  const toggleproductType = (productType: {
    id: ProductType | string;
    name: string;
  }) => {
    setProductType(productType);
  };

  const loadLazyData = async (lazyParams: any) => {
    setIsLoading(true);
    const res = await dispatch(
      findFilteredOrganisationProducts({
        ...lazyParams,
        productType: productType?.id,
      })
    );
    setIsLoading(false);
    setLazyParams(lazyParams);
  };

  useEffect(() => {
    loadLazyData(lazyParams);
  }, [productType]);

  const onPage = (event: any) => {
    setLazyParams({
      filters: lazyParams.filters,
      first: event.first,
      rows: event.rows,
      page: event.page,
    });

    loadLazyData({
      filters: lazyParams.filters,
      first: event.first,
      rows: event.rows,
      page: event.page,
    });
  };
  const priceBodyTemplate = (product: { price: number }) => {
    return (
      <p className="text-[#0C4654] font-[500]">{`${CurrencySign} ${formatAmount(product.price)}`}</p>
    );
  };
  const lastEditedBodyTemplate = (product: { updatedAt: string }) => {
    return moment(product?.updatedAt).format("l");
  };
  const nameBodyTemplate = (product: { name: string }) => {
    return (
      <div className="flex items-center gap-2">
        <img src={GreenDotIcon} alt="green dot icon" className="" />
        <p> {product?.name}</p>
      </div>
    );
  };
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    if(e.target.value.length >= 3){
      let _filters = { ...lazyParams.filters };
      _filters["global"].value = value;
      setGlobalFilterValue(value);
      setLazyParams({ ...lazyParams, filters: _filters });
      loadLazyData({
        ...lazyParams,
        filters: _filters,
      });
    }
    if(e.target.value === "") {
      loadLazyData({
        ...lazyParams,
        filters: {
          global: { value: "", matchMode: FilterMatchMode.CONTAINS },
        },
      });
    }
  };

  return (
    <>
      <OrganisationLayout>
        {priceListData?.products?.length < 0 ? (
          <Spinner spinnerText={""} styles={""} />
        ) : (
          <div className="px-3 lg:px-12 mt-5">
            <div className="mb-6">
              <h1 className="text-[#0C4654] font-[500] text-[1.2rem]">
                Healthtracka’s price List
              </h1>
            </div>
            <div className="w-fit mb-6 rounded bg-[#F2F6FA] p-2 text-[#6B7381]">
              <BaseToggleButton
                items={productTypes}
                activeTab={productType}
                setActiveTab={toggleproductType}
              />
            </div>
            <div className="bg-[#F2F6FA] rounded px-4 py-6 text-[#0C4654]">
              <div className="mb-4 px-3">
                <label className="relative">
                  <BiSearchAlt
                    className="textbrandColor absolute left-3 top-1"
                    size="1rem"
                  />
                  <input
                    type="text"
                    className="border p-4 pl-8 rounded-[0.5rem] w-full text-[0.875rem] font-[500] placeholder-[#767B86] focus:outline-none"
                    placeholder="Search"
                    onKeyUp={onGlobalFilterChange}
                  />
                </label>
              </div>
              <div className="p-3 mt-2 rounded-[8px]">
                <DataTable
                  value={priceListData?.products}
                  lazy
                  paginator
                  paginatorClassName="text-[#0C4654]"
                  className="h-full"
                  first={lazyParams?.first ?? 0}
                  rows={lazyParams?.rows ?? 0}
                  totalRecords={priceListData?.total ?? 0}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  dataKey="id"
                  rowHover
                  onPage={onPage}
                  scrollable
                  loading={isLoading}
                  selectionMode="single"
                  responsiveLayout="scroll"
                  globalFilterFields={["name"]}
                  emptyMessage="No products found."
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
                >
                  <Column
                    field="name"
                    header="Name"
                    style={{ minWidth: "12rem" }}
                    body={nameBodyTemplate}
                    bodyClassName={"text-[#0C4654] font-[500]"}
                    headerStyle={headerStyle}
                    bodyStyle={bodyStyle}
                  />
                  <Column
                    header="Price"
                    filterField="price"
                    style={{ minWidth: "12rem" }}
                    body={priceBodyTemplate}
                    headerStyle={headerStyle}
                    bodyStyle={bodyStyle}
                  />
                  <Column
                    header="Last Edited"
                    //   filterField="representative"
                    showFilterMenu={false}
                    filterMenuStyle={{ width: "14rem" }}
                    style={{ minWidth: "14rem" }}
                    bodyClassName={"text-[#0C4654] font-[500]"}
                    body={lastEditedBodyTemplate}
                    headerStyle={headerStyle}
                    bodyStyle={bodyStyle}
                  />
                </DataTable>
              </div>

              {/* <DataTable
                value={allProducts}
                // paginator
                // rows={10}
                dataKey="id"
             
               
                loading={loading}
              
                emptyMessage="No products found."
                // header={header}
              >
                <Column
                  field="name"
                  header="Name"
                  style={{ minWidth: "12rem" }}
                />
                <Column
                  header="Price"
                  filterField="price"
                  style={{ minWidth: "12rem" }}
                  body={priceBodyTemplate}
                />
                <Column
                  header="Last Edited"
                  //   filterField="representative"
                  showFilterMenu={false}
                  filterMenuStyle={{ width: "14rem" }}
                  style={{ minWidth: "14rem" }}
                  body={lastEditedBodyTemplate}
                />
              </DataTable> */}
            </div>
          </div>
        )}
      </OrganisationLayout>
    </>
  );
}

export default PriceList;
